import { Avatar as A } from 'radix-ui';
import { cn, initials } from '@/lib/utils';

/** Cor estável por nome (paleta de identidade), para avatares sem foto. */
const TINTS = ['cyan', 'amber', 'lavender', 'mint', 'coral', 'sky', 'rose', 'sand'] as const;
function tintFor(name: string) {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TINTS[h % TINTS.length]!;
}

const SIZES = {
  xs: 'size-6 text-[10px]',
  sm: 'size-8 text-[11.5px]',
  md: 'size-10 text-[13px]',
  lg: 'size-14 text-[17px]',
};

export function Avatar({
  name,
  src,
  size = 'sm',
  className,
}: {
  name: string;
  src?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const tint = tintFor(name);
  return (
    <A.Root
      className={cn('relative inline-flex shrink-0 overflow-hidden rounded-full', SIZES[size], className)}
    >
      {src && <A.Image src={src} alt="" className="size-full object-cover" />}
      <A.Fallback
        className="flex size-full items-center justify-center font-semibold tracking-tight"
        style={{
          background: `color-mix(in srgb, var(--color-label-${tint}) 22%, var(--color-raised))`,
          color: `var(--color-label-${tint})`,
        }}
      >
        {initials(name)}
      </A.Fallback>
    </A.Root>
  );
}

export function AvatarStack({
  people,
  max = 4,
  size = 'xs',
}: {
  people: { name: string; src?: string | null }[];
  max?: number;
  size?: keyof typeof SIZES;
}) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <div className="flex -space-x-1.5">
      {shown.map((p, i) => (
        <Avatar key={i} name={p.name} src={p.src} size={size} className="ring-2 ring-stage" />
      ))}
      {rest > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-overlay font-medium text-fg-muted ring-2 ring-stage',
            SIZES[size],
          )}
        >
          +{rest}
        </span>
      )}
    </div>
  );
}
